import { makeAutoObservable, runInAction } from 'mobx'
import Router, { SingletonRouter } from 'next/router'
import { createContext } from 'react'

import Communication from '../../shared-lib/communication/Communication'
import ErrorHandler from '../../lib/ErrorHandler'
import getApiUrl from '../../shared-lib/communication/getApiUrl'
import ApplicationStore, { $ApplicationStore } from './ApplicationStore'
import UiStore, { $UiStore } from './UiStore'
import I18n from '../../shared-lib/i18n/i18n'
import languages from '../../languages'

declare global {
  interface Window {
    analytics: any
  }
}

export type $RootStore = RootStore

class RootStore {
  ready = false
  communication!: Communication
  i18n: I18n
  router: SingletonRouter
  config: any = {}
  applicationStore: $ApplicationStore
  uiStore: $UiStore
  errorHandler: ErrorHandler

  constructor(router: SingletonRouter) {
    this.router = router
    this.applicationStore = new ApplicationStore(this)
    this.uiStore = new UiStore()
    this.i18n = new I18n(languages, 'en')
    this.errorHandler = new ErrorHandler(this)

    makeAutoObservable(this)
  }

  loadStore = async () => {
    const isApiUp = await this.checkApiIsUp()
    if (!isApiUp) return

    this.populateFromStoreAndRedirect()
  }

  loadCommunication = async (config: any) => {
    // Get API url and load config
    try {
      const { apiHost } = await getApiUrl({
        apiDefault: config.apiDefault,
        platform: config.platform,
        appVersion: config.appVersion,
        useSecure: process.env.SS_ENV === 'development' ? '' : 's',
        override: config.override,
      })

      config.apiHost = apiHost
    } catch (e) {
      return false
    }

    this.communication = new Communication(this, {
      apiHost: config.apiHost,
      platform: config.platform,
      env: config.env,
      timezone: config.timezone,
      addNetworkLatency: 0,
    })

    runInAction(() => {
      this.ready = true
    })

    return true
  }

  checkApiIsUp = async (): Promise<boolean> => {
    return true
  }

  populateFromStoreAndRedirect = async () => {
    const { applicationStore: { formatIdString } } = this
    let emailAddress = window.localStorage.getItem('emailAddress')
    let id = window.localStorage.getItem('id')
    window.localStorage.removeItem('authToken')

    // Checking if query params exist, load application from query instead of storage to allow override
    if (window.location.search) {
      const params = new URLSearchParams(window.location.search)
      id = params.get('id')
      emailAddress = params.get('email')
    }

    if (!!id && !!emailAddress) {
      await this.applicationStore.loadApplication(formatIdString(id), emailAddress)

      if (this.applicationStore.applicationId && !window.location.href.includes('callback')) {
        this.router.push('/apply/overview')
        return
      }
    }
  }

  get authStore() {
    const authToken = localStorage.getItem('authToken')
    const auth = authToken ? JSON.parse(authToken).auth : null

    return {
      getAccessAuthToken: (_operationName: string) => {
        return {
          auth,
        }
      },
      authToken: {
        auth,
      },
      sessionExpired: false,
    }
  }
}

export default createContext(new RootStore(Router))